/**
 * Fechar um chamado que é de outra pessoa: perguntar antes de quem fica.
 *
 * ## O pedido
 *
 * Da Gisele: ao finalizar um chamado atribuído a outro agente, perguntar se
 * quem está fechando quer assumi-lo. Nas palavras dela, *"às vezes vamos
 * fechar com o nome do responsável mesmo, às vezes já vamos fechar no nome
 * deles"* — ou seja, **os dois caminhos são legítimos**, e por isso é uma
 * pergunta e não uma regra.
 *
 * Hoje quem tem a permissão de gerenciar chamados fecha em silêncio e o
 * chamado fica com o responsável antigo. Não é errado; é só que ninguém
 * escolheu.
 *
 * ## Por que esta regra mora fora do componente
 *
 * `TicketDetails` tem mais de seis mil linhas e nenhum teste, e finaliza o
 * chamado por DOIS caminhos (`handleStatusJustificationChange` e
 * `handleInputChange`) — que já duplicam entre si a guarda de serviço que
 * bloqueia o fechamento. Uma terceira cópia da decisão divergiria no primeiro
 * conserto feito só num dos dois, e divergir aqui significa o modal aparecer
 * por um caminho e não pelo outro.
 */

/**
 * Os estágios que CONCLUEM um chamado.
 *
 * Os mesmos nomes de `TicketStatus::NOMES_FINALIZADOS` na api -- não é uma
 * lista nova. Inventar aqui uma noção própria de "fechado" faria a tela e o
 * servidor discordarem sobre o que é fechar, e o sintoma seria o modal
 * aparecer em situações que o servidor não considera fechamento.
 */
export const NOMES_FINALIZADOS = ['Resolvido', 'Cancelado', 'Fechado'];

export const ehStatusQueFinaliza = (statusId, ticketStatuses) => {
    if (statusId == null || statusId === '') return false;

    const st = (ticketStatuses || []).find((s) => Number(s?.id) === Number(statusId));

    return !!st && NOMES_FINALIZADOS.includes(st.name);
};

/**
 * O mesmo, para o código LEGADO de status.
 *
 * A tela fecha o chamado por dois controles, e eles falam línguas
 * diferentes: o seletor do topo manda `status_id` (a tabela nova), e o
 * "Fechar ticket..." ao lado do comentário manda o `status` numérico antigo.
 * Os dois fecham de verdade, então os dois precisam da pergunta — cobrir só
 * um faria o modal aparecer por um caminho e não pelo outro, que é pior que
 * não ter modal: vira regra que a pessoa não consegue prever.
 *
 * O mapa é o da api (`TicketController`): 0=Fechado, 1=Aberto, 2=Pendente,
 * 3=Resolvido, 4=Cancelado.
 */
export const CODIGOS_LEGADOS_QUE_FINALIZAM = [0, 3, 4];

export const ehStatusLegadoQueFinaliza = (codigo) => {
    if (codigo == null || codigo === '') return false;

    return CODIGOS_LEGADOS_QUE_FINALIZAM.includes(Number(codigo));
};

/**
 * Deve perguntar se quem está fechando quer assumir o chamado?
 *
 * Só quando as três coisas valem ao mesmo tempo:
 *
 * 1. o status novo FINALIZA o chamado — mudar de "Aberto" para "Pendente" não
 *    é fechar, e perguntar ali seria um modal no meio do trabalho normal;
 * 2. existe um responsável — chamado sem dono não tem de quem assumir, e a
 *    pergunta não faria sentido;
 * 3. o responsável é OUTRA pessoa — fechar o próprio chamado é o caso comum,
 *    e um modal nele transformaria a funcionalidade num estorvo diário.
 *
 * Aceita `statusId` (tabela nova) OU `statusLegado` (o número antigo), porque
 * os dois controles de fechamento da tela falam línguas diferentes.
 *
 * @param {{statusId?: *, statusLegado?: *, ticketStatuses?: Array, assignedTo: *, currentUserId: *}} entrada
 */
export const precisaPerguntarSeAssume = ({
    statusId,
    statusLegado,
    ticketStatuses,
    assignedTo,
    currentUserId,
} = {}) => {
    const finaliza = ehStatusQueFinaliza(statusId, ticketStatuses)
        || ehStatusLegadoQueFinaliza(statusLegado);

    if (!finaliza) return false;

    // `assigned_to` chega ora como id, ora como objeto, dependendo de a tela
    // ter acabado de recarregar do servidor ou não. As duas formas são o
    // mesmo responsável, e tratar só uma faria a pergunta sumir pela metade
    // dos caminhos -- sem erro nenhum.
    //
    // O teste de tipo em vez de `assignedTo?.id ?? assignedTo`: com
    // `{ id: null }` aquele `??` cai para o OBJETO inteiro, `Number(objeto)`
    // dá `NaN`, e `NaN !== id` é verdadeiro -- ou seja, perguntaria num
    // chamado sem responsável. Foi o teste que achou.
    const responsavel = (assignedTo && typeof assignedTo === 'object')
        ? assignedTo.id
        : assignedTo;

    if (responsavel == null || responsavel === '') return false;
    if (currentUserId == null || currentUserId === '') return false;

    return Number(responsavel) !== Number(currentUserId);
};
